class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("input", this.onChange.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((el) => el.offsetParent !== null);

    let selectedIndex = selectedElement ? allVisibleElements.indexOf(selectedElement) : -1;
    let nextIndex = selectedIndex;

    if (moveUp) {
      nextIndex = selectedIndex <= 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
    } else {
      nextIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
    }

    if (selectedIndex !== -1) allVisibleElements[selectedIndex].setAttribute("aria-selected", false);
    const nextEl = allVisibleElements[nextIndex];
    if (nextEl) nextEl.setAttribute("aria-selected", true);
    this.input.setAttribute("aria-activedescendant", nextEl?.id || "");
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    let products;

    // Use cached product data if available
    if (this.cachedResults[queryKey]) {
      products = this.cachedResults[queryKey];
    } else {
      const baseTerm = `${searchTerm.trim()}*`;
      const query = `/search/suggest.json?q=${encodeURIComponent(baseTerm + " visible-in-search")}&resources[type]=product&resources[options][fields]=title,tag,variants.title`;

      try {
        const response = await fetch(query, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();

        products = data.resources.results.products || [];
        products = products.filter((p) => p.tags?.includes("visible-in-search"));

        // Cache raw product data only
        this.cachedResults[queryKey] = products;
      } catch (error) {
        if (error?.code === 20) return;
        this.close();
        throw error;
      }
    }

    const resultList = this.querySelector("#predictive-search-results-products-list");
    const template = this.querySelector("#predictive-search-product-template");

    if (!resultList || !template) { this.close(); return; }

    resultList.innerHTML = "";

    if (products.length === 0) {
      const emptyItem = document.createElement("li");
      emptyItem.className = "predictive-search__list-item";
      emptyItem.textContent = "No matching products found";
      resultList.appendChild(emptyItem);
      this.setAttribute("results", true);
      this.open();
      return;
    }

    const colorWheelMap = {
      "multi-purpose-paint-1l-grey": "https://cdn.shopify.com/s/files/1/2162/6221/files/Packshot_of_RAL_Wheel_Multi-Purpose_Paint.png?v=1747408088",
      "floor-paint-anti-slip-grey": "https://cdn.shopify.com/s/files/1/2162/6221/files/Packshot_of_RAL_Wheel_Floor_Paint_Anti_Slip.png?v=1747408518",
      "floor-paint-grey": "https://cdn.shopify.com/s/files/1/2162/6221/files/Packshot_of_RAL_Wheel_Floor_Paint.png?v=1747408617",
      "epoxy-floor-paint-anti-slip-grey": "https://cdn.shopify.com/s/files/1/2162/6221/files/Packshot_of_RAL_Wheel_Epoxy_Floor_Paint_Anti_Slip.png?v=1747408669",
      "epoxy-floor-paint-grey": "https://cdn.shopify.com/s/files/1/2162/6221/files/Packshot_of_RAL_Wheel_Epoxy_Floor_Paint.png?v=1747408744",
    };

    products.forEach((product, index) => {
      const clone = template.cloneNode(true);
      clone.removeAttribute("hidden");
      clone.id = `predictive-search-option-product-${index}`;

      const anchor = clone.querySelector("a");
      anchor.href = product.url;

      const img = clone.querySelector("img");
      const handle = product.handle;

      // Override image logic
      const overrideImage = colorWheelMap[handle];
      if (overrideImage) {
        img.src = overrideImage;
        img.alt = `Colour options for ${product.title}`;
      } else if (product.image) {
        img.src = product.image;
        img.alt = product.title;
      } else {
        img.remove();
      }

      const titleEl = clone.querySelector("p");
      let titleText = product.title;

      titleText = titleText.replace(
        /2\.5L\s*-\s*Grey/i,
        "Available in Multiple Colours/Sizes"
      );
      titleText = titleText.replace(
        /1L\s*-\s*Grey/i,
        "Available in Multiple Colours"
      );

      titleEl.textContent = titleText;

      const priceWithVAT = clone.querySelector(".price__regular--with-vat");
      const priceExVAT = clone.querySelector(".price__regular--ex-vat");
      const compareWithVAT = clone.querySelector(".price__compare--with-vat");
      const compareExVAT = clone.querySelector(".price__compare--ex-vat");
      const savingsEl = clone.querySelector(".price__savings");
      const fromLabel = clone.querySelector(".price__from-label");
      const fromLabel2 = clone.querySelector(".price__from-label--ex-vat");

      const price = product.price;
      const compareAtPrice = product.compare_at_price || 0;
      const currency = product.currency || "GBP";
      const vatRate = 1.2;

      const formatMoney = (cents) => cents.toLocaleString(undefined, { style: "currency", currency });

      // Render basic price
      priceWithVAT.textContent = formatMoney(price * 1);
      priceExVAT.textContent = formatMoney(price / vatRate);

      if (compareAtPrice > price) {
        compareWithVAT.textContent = formatMoney(compareAtPrice);
        compareExVAT.textContent = formatMoney(compareAtPrice / vatRate);
        const savings = compareAtPrice - price;
        savingsEl.textContent = `Save ${formatMoney(savings)}`;
      } else {
        compareWithVAT.textContent = "";
        compareExVAT.textContent = "";
        savingsEl.textContent = "";
      }

      resultList.appendChild(clone);

      // --- SECONDARY AJAX CALL to add 'From' labels ---
      fetch(`/products/${handle}.js`)
        .then((res) => res.json())
        .then((data) => {
          const variantPrices = data.variants.map((v) => v.price);
          const hasOtherSizes = /2\.5L\s*-\s*Grey/i.test(data.title);
          const hasMultiplePrices = new Set(variantPrices).size > 1 || hasOtherSizes;


          if (fromLabel) {
            if (hasMultiplePrices) {
              console.log("Multiple prices found for", handle);
              fromLabel.style.display = "inline";
              fromLabel2.style.display = "inline";
            } else {
              console.log("Single price for", handle);
              fromLabel.style.display = "none";
              fromLabel2.style.display = "none";
            }
          }
        })
        .catch((err) => {
          console.error(`Failed to fetch variants for ${handle}:`, err);
        });
    });

    this.setAttribute("results", true);
    this.open();
  }




  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    setTimeout(() => this.statusElement.setAttribute("aria-hidden", "true"), 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
